import { useState, useEffect } from 'react';
import { KovrificationConfig } from '../../types/creation';
import { CaptionsStyleSelector } from '../creation/CaptionsStyleSelector';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';
import { messages } from '../../messages';

// Import template images
import template60 from '../../assets/templates/c-roll/60.png';
import template70 from '../../assets/templates/c-roll/70.png';
import template75 from '../../assets/templates/c-roll/75.png';
import template80 from '../../assets/templates/c-roll/80.png';

interface KovrificationCreationConfigProps {
  config: KovrificationConfig;
  onConfigChange: (config: KovrificationConfig) => void;
}

/**
 * KovrificationCreationConfig component
 * 
 * This component handles the configuration UI for the kovrification strategy (known as "C-roll" in the frontend)
 * during the creation flow. It allows users to modify settings before processing.
 */
export function KovrificationCreationConfig({ config, onConfigChange }: KovrificationCreationConfigProps) {
  const [customProportion, setCustomProportion] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const { t } = useTranslation();

  // Effect to set selected template based on current config proportion
  useEffect(() => {
    // Find the closest template to the current proportion
    const proportion = config.video_processing.vertical_video_proportion;
    if (Math.abs(proportion - 0.6) < 0.01) {
      setSelectedTemplate('template-60');
    } else if (Math.abs(proportion - 0.7) < 0.01) {
      setSelectedTemplate('template-70');
    } else if (Math.abs(proportion - 0.75) < 0.01) {
      setSelectedTemplate('template-75');
    } else if (Math.abs(proportion - 0.8) < 0.01) {
      setSelectedTemplate('template-80');
    } else {
      setSelectedTemplate(null);
      setCustomProportion(true);
    }
  }, [config.video_processing.vertical_video_proportion]);

  const toggleCaptions = () => {
    onConfigChange({
      ...config,
      caption: {
        ...config.caption,
        enabled: !config.caption.enabled
      }
    });
  };

  const handleColorChange = (type: 'color' | 'accent_color', value: string | undefined) => {
    onConfigChange({
      ...config,
      caption: {
        ...config.caption,
        [type]: value,
        // If colors are being customized, reset font sizes unless they have their own UI
        font_size: undefined,
        accent_font_size: undefined,
      }
    });
  };

  const handleFontSizeChange = (type: 'font_size' | 'accent_font_size', value: number | undefined) => {
    onConfigChange({
      ...config,
      caption: {
        ...config.caption,
        [type]: value,
      }
    });
  };

  const handleResetCustomizations = () => {
    onConfigChange({
      ...config,
      caption: {
        // Preserve style_id and other non-customizable fields
        enabled: config.caption.enabled, // Keep enabled status
        style_id: config.caption.style_id,
        words_per_line: config.caption.words_per_line, // Assuming this is not part of color/font customization UI here
        vertical_margin: config.caption.vertical_margin, // Assuming this is not part of color/font customization UI here
        // Reset color and font related properties
        color: undefined,
        accent_color: undefined,
        font_size: undefined,
        accent_font_size: undefined,
      }
    });
  };

  const handleStyleChange = (styleId: string) => {
    onConfigChange({
      ...config,
      caption: {
        ...config.caption,
        style_id: styleId
      }
    });
    posthog.capture('custom_caption_chosen', { strategy_type: 'kovrification', style_id: styleId });
  };

  const handleVideoProportionChange = (value: number) => {
    onConfigChange({
      ...config,
      video_processing: {
        ...config.video_processing,
        vertical_video_proportion: value
      }
    });
  };

  // Define template configurations
  const TEMPLATES = [
    {
      id: 'template-60',
      name: t('kovrificationCreationConfig.templateName', { percent: 60 }),
      image: template60,
      config: {
        video_processing: {
          vertical_video_proportion: 0.6
        }
      }
    },
    {
      id: 'template-70',
      name: t('kovrificationCreationConfig.templateName', { percent: 70 }),
      image: template70,
      config: {
        video_processing: {
          vertical_video_proportion: 0.7
        }
      }
    },
    {
      id: 'template-75',
      name: t('kovrificationCreationConfig.templateName', { percent: 75 }),
      image: template75,
      config: {
        video_processing: {
          vertical_video_proportion: 0.75
        }
      }
    },
    {
      id: 'template-80',
      name: t('kovrificationCreationConfig.templateName', { percent: 80 }),
      image: template80,
      config: {
        video_processing: {
          vertical_video_proportion: 0.8
        }
      }
    }
  ];

  const handleTemplateSelect = (templateId: string) => {
    const template = TEMPLATES.find(t => t.id === templateId);
    if (template) {
      // Apply video processing settings from the template
      handleVideoProportionChange(template.config.video_processing.vertical_video_proportion);
      setSelectedTemplate(templateId);
      setCustomProportion(false);
    }
  };

  return (
    <div className="md:grid md:grid-cols-2 md:gap-6 w-full">
      {/* Captions Section */}
      <div className="mb-6 md:mb-0 p-4 lg:p-6 rounded-2xl bg-surface-interactive dark:bg-surface-interactive-dark shadow-interactive dark:border dark:border-interactive-dark flex flex-col h-full md:min-h-[500px]">
        <h2 className="text-lg font-medium mb-3 text-content-strong">{t('kovrificationCreationConfig.captionsTitle')}</h2>
        
        <div className="flex items-center mb-5">
          <div className="w-5 h-5 mr-2 relative">
            <input
              type="checkbox"
              id="enable-captions"
              checked={config.caption.enabled}
              onChange={toggleCaptions}
              className="sr-only peer"
            />
            <label 
              htmlFor="enable-captions" 
              className="flex items-center justify-center w-5 h-5 rounded peer-checked:bg-primary bg-input dark:bg-input-dark border border-input dark:border-input-dark peer-checked:border-primary cursor-pointer"
            >
              {config.caption.enabled && (
                <span className="material-symbols-outlined text-on-primary text-xs">check</span>
              )}
            </label>
          </div>
          <label htmlFor="enable-captions" className="text-sm text-body cursor-pointer">{t('kovrificationCreationConfig.enableCaptionsLabel')}</label>
        </div>

        {config.caption.enabled ? (
          <div className="flex-grow flex flex-col">
          <CaptionsStyleSelector
            selected={config.caption.style_id || 'karaoke_default_v1'}
            onSelectStyle={handleStyleChange}
            color={config.caption.color}
            accentColor={config.caption.accent_color}
            fontSize={config.caption.font_size}
            accentFontSize={config.caption.accent_font_size}
            onColorChange={handleColorChange}
            onFontSizeChange={handleFontSizeChange}
            onResetCustomizations={handleResetCustomizations}
          />
          </div>
        ) : (
          <div className="flex-grow flex items-center justify-center">
            <div className="text-center text-muted">
              <span className="material-symbols-outlined text-4xl mb-2">closed_caption_off</span>
              <p>{t('kovrificationCreationConfig.captionsDisabledPlaceholder')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Vertical Video Proportion Section */}
      <div className="mb-6 p-4 lg:p-6 rounded-2xl bg-surface-interactive dark:bg-surface-interactive-dark shadow-interactive dark:border dark:border-interactive-dark md:min-h-[500px] flex flex-col">
        <h2 className="text-lg font-medium mb-3 text-content-strong">{t('kovrificationCreationConfig.proportionTitle')}</h2>
        
        {/* Template grid with images - responsive grid for different screen sizes */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 flex-grow">
          {TEMPLATES.map(template => (
            <div 
              key={template.id}
              className={`relative aspect-[9/16] rounded-lg overflow-hidden cursor-pointer ${selectedTemplate === template.id ? 'ring-3 ring-primary' : 'hover:opacity-90'} transition-all max-w-[180px] mx-auto w-full`}
              onClick={() => handleTemplateSelect(template.id)}
            >
              <img 
                src={template.image} 
                alt={template.name} 
                className="w-full h-full object-cover"
              />
              
              {/* Arrow indicator showing the exact proportion position */}
              <div 
                className="absolute right-0 flex items-center" 
                style={{ 
                  top: `${template.config.video_processing.vertical_video_proportion * 100}%`,
                  transform: 'translateY(-50%)'
                }}
              >
                <div className="bg-overlay-default hover:bg-primary-alpha text-on-overlay text-[10px] py-0.5 px-1 rounded-l-sm flex items-center transition-colors">
                  <span className="material-symbols-outlined text-[10px] mr-0.5">arrow_left</span>
                  <span>{Math.round(template.config.video_processing.vertical_video_proportion * 100)}%</span>
                </div>
              </div>
              
              {selectedTemplate === template.id && (
                <div className="absolute top-2 right-2 bg-surface-alpha rounded-full">
                  <span className="material-symbols-outlined text-primary p-1">check_circle</span>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Custom option */}
        <button 
          onClick={() => {
            setSelectedTemplate(null);
            setCustomProportion(true);
          }}
          className={`w-full py-3 px-4 rounded-lg mb-4 flex items-center justify-center gap-2 ${customProportion 
              ? 'bg-primary text-on-primary' 
              : 'bg-button-neutral dark:bg-button-neutral-dark text-on-button-neutral'
          } transition-colors`}
        >
          <span className="material-symbols-outlined">tune</span>
          <span className="font-medium">{t('kovrificationCreationConfig.customizeButton')}</span>
        </button>
        
        {/* Custom proportion slider */}
        {customProportion && (
          <div className="flex flex-col mt-4">
            <div className="flex justify-between mb-2">
              <label className="text-sm text-body">{t('kovrificationCreationConfig.proportionSliderLabel')}</label>
              <span className="text-sm text-body">{Math.round(config.video_processing.vertical_video_proportion * 100)}%</span>
            </div>
            
            <input
              type="range"
              min="0.5"
              max="0.9"
              step="0.01"
              value={config.video_processing.vertical_video_proportion}
              onChange={(e) => handleVideoProportionChange(parseFloat(e.target.value))}
              className="w-full h-2 bg-input-track dark:bg-input-track-dark rounded-lg appearance-none cursor-pointer accent-primary"
            />
            
            <div className="text-xs text-caption mt-2">
              {messages.create.configuration.videoProcessing.recommended}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 